// OPERATOR-CONSOLE in the in-memory API: Operator › Settings (services/api operator console
// routes) with the real routes' rules —
// - who: a platform admin (an agency ADMIN whose agency holds an operator grant on HTVA); another
//   agency user is 403, anyone else 404;
// - settings: a PUT stores a new version at its scope (platform, a company or a satellite) and
//   moves the pointer; `value: null` clears an override (still a version, value null); the same
//   value again stores nothing; rollback moves the pointer to an earlier version of the same scope
//   (no new version). What applies: satellite > company > platform > the shipped default. Keys kept
//   in their own stores are history only (`editable: false`; a PUT is 409);
// - the baseline is rendered from the settings; the roster from the DEs' placements and the
//   company's `roster_overrides`;
// - operator agents: Deployer (active), Fixer and Builder (paused). A sealed key is never
//   returned (only `has_key` and its last four); a job token is shown once (`cosop_…`);
// - jobs: there is no runner in the twin, so a Deployer job is answered as the runner would leave
//   it: done with its checklist filled, or failed at the step that cannot pass (an unknown
//   container); Retry queues a failed job again.
import {
  AG_BASE_PROMPT,
  baseRuleRows,
  baseRulesMarkdown,
  DE_BASE_PROMPT,
  DE_BASE_RULES_VERSION,
} from "@companyos/domain-agents";
import {
  ApplyBaselineRequest,
  CompanyFromPackRequest,
  OperatorAgentKeyPut,
  OperatorAgentPut,
  OperatorJobTokenCreate,
  ProvisioningSettingValues,
  SETTING_SCOPES,
} from "@companyos/schema/contracts";
import type { OperationOutput, RouteId } from "@companyos/sdk";
import { type Handler, json, notFound, problem, validation } from "./fake";
import { COMPANY_IDS, DE_IDS, PLATFORM_AGENCY_IDS, uid } from "./fake-data";
import type { MeResponse } from "./types";

type Detail = OperationOutput<"operator.settings.get">;
type Key = Detail["key"];
type Scope = Detail["scope"];
type AgentView = OperationOutput<"operator.agents.list">["agents"][number];
type Job = OperationOutput<"operator.jobs.get">;
type Step = Job["steps"][number];
type Api = AgentView["allowed_apis"][number];

export const FAKE_OPERATOR_CONSOLE_ROUTES = [
  "operator.settings.list",
  "operator.settings.get",
  "operator.settings.put",
  "operator.settings.rollback",
  "operator.baseline.get",
  "operator.roster.get",
  "operator.margin.models",
  "operator.agents.list",
  "operator.agents.put",
  "operator.agents.key.put",
  "operator.agents.tokens.create",
  "operator.agents.tokens.revoke",
  "operator.jobs.list",
  "operator.jobs.get",
  "operator.jobs.retry",
  "operator.deployer.companyFromPack",
  "operator.deployer.applyBaseline",
  // DE-BASE-RULES: read by Settings › Base prompt.
  "operator.baseRules.get",
] as const satisfies readonly RouteId[];

const ALL_KEYS: readonly Key[] = [
  "de_defaults",
  "budget_rollover",
  "feature_rules",
  "wake_intake",
  "system_agent",
  "bridge_runtime",
  "satellite_runtime",
  "line_switches",
  "memory_lane",
  "models",
  "runtime_profiles",
  "container_memory",
  "baseline",
  "roster_overrides",
  "base_prompt",
  "skills_defaults",
  "rollout",
  "limits",
  "nightly_review",
  "failure_stop",
  "skillsmith",
  "recall_tenant",
  "live_canvas",
  "task_budgets",
  "effort_defaults",
  "loop_breaker",
  "memory_retention",
  "model_provider_map",
  "plan_model_access",
  "satellite",
];
/** Kept in their own stores: the console shows their history only. */
const OWN_STORE: readonly Key[] = [
  "effort_defaults",
  "loop_breaker",
  "memory_retention",
  "model_provider_map",
  "plan_model_access",
  "satellite",
];
type ProvKey = keyof typeof ProvisioningSettingValues;
const isProv = (k: Key): k is ProvKey => k in ProvisioningSettingValues;
/** The scopes of the history-only keys (the provisioning keys' come from SETTING_SCOPES). */
const OWN_STORE_SCOPES: Record<string, readonly Scope[]> = {
  effort_defaults: ["platform"],
  loop_breaker: ["platform", "company"],
  memory_retention: ["platform"],
  model_provider_map: ["platform"],
  plan_model_access: ["platform"],
  satellite: ["satellite"],
};
const scopesOf = (k: Key): readonly Scope[] =>
  isProv(k) ? SETTING_SCOPES[k] : (OWN_STORE_SCOPES[k] ?? ["platform"]);

/** The shipped defaults (services/api/src/domain/provisioning/settings.ts PROVISIONING_DEFAULTS). */
export const FAKE_SETTING_DEFAULTS: Record<ProvKey, unknown> = {
  de_defaults: {
    enabled: true,
    budget: {
      amount_minor: 500,
      hard_cap: true,
      soft_thresholds: ["0.5", "0.8"],
      alert_only: false,
    },
    key_scopes: ["boards:*", "files:*", "kpis:*", "runs:*", "tasks:*"],
    general_space: "editor",
    runtime: "none",
    cos_digest_template_keys: ["chief-of-staff"],
    first_task: {
      board_name: "Onboarding",
      title: "First check-in: confirm your role and tools",
      body:
        "Internal check-in: no client or outside contact. Read this task, then post one comment on " +
        "it with your role in one sentence, the KPIs named on your seat, and one thing you found " +
        "in your memory (or 'nothing yet'). Then complete the task. Do not send, publish or " +
        "contact anyone.",
    },
  },
  budget_rollover: { enabled: true, repeat_monthly_default: true },
  feature_rules: {
    auto_apply: false,
    rules: [
      { feature: "kpi", when: [{ kind: "always" }] },
      { feature: "chat", when: [{ kind: "always" }] },
      { feature: "agents", when: [{ kind: "de" }] },
      { feature: "memory", when: [{ kind: "de" }] },
    ],
  },
  wake_intake: { enabled: true, overlay_only: false },
  system_agent: {
    enabled: true,
    display_name: "System",
    key_scopes: ["signals:read", "feedback:write", "space:write"],
  },
  bridge_runtime: {
    poll_seconds: 30,
    heartbeat_seconds: 20,
    lease_seconds: 60,
    run_timeout_seconds: 900,
    run_binding: "key",
    run_via: "gateway",
    steer_poll_seconds: 10,
    kanban_wake: { enabled: true, max_wakes_per_hour: 12, min_gap_seconds: 20 },
    files: { inbox_dir: "/opt/data/inbox/chat", outbox_dir: "/opt/data/outbox/chat" },
    fallback_model: null,
  },
  line_switches: {
    rules: [
      { switch: "design_line", when: [{ kind: "skill", key: "design-line-routing" }] },
      { switch: "video_line", when: [{ kind: "skill", key: "video-line" }] },
      { switch: "render_service", when: [{ kind: "skill", key: "render-visuals" }] },
      {
        switch: "external_decide",
        when: [{ kind: "integration", provider: "openrouter" }],
        note: "decide on an external classifier when the OpenRouter connection exists",
      },
    ],
  },
  memory_lane: { escalation_days: 3 },
  satellite_runtime: {
    wake_port: 8660,
    config_poll_seconds: 300,
    releases: { bridge: null, render_service: null },
  },
  models: { kinds: [], aux_vision_model: null },
  runtime_profiles: {
    light: {
      max_sessions: 2,
      max_runs: 2,
      kanban_max_in_progress: 4,
      dispatch_interval_seconds: 10,
    },
    standard: {
      max_sessions: 4,
      max_runs: 4,
      kanban_max_in_progress: 6,
      dispatch_interval_seconds: 10,
    },
    heavy: {
      max_sessions: 4,
      max_runs: 4,
      kanban_max_in_progress: 6,
      dispatch_interval_seconds: 10,
    },
  },
  container_memory: { light: 1024, standard: 2048, heavy: 4096 },
  baseline: { version: null, profile: "standard", containers: [] },
  roster_overrides: { des: [] },
  base_prompt: { version: null },
  skills_defaults: { packs: [] },
  rollout: {
    canary_company_id: null,
    volunteer_company_id: null,
    window_hours: 48,
    window_runs: 200,
    auto_stop_factor: 1.5,
    wave_order: [],
  },
  limits: {
    company_envelope_minor: null,
    hard_cap: true,
    system_agent_daily_cap_micros: 2_000_000,
    operator_agent_daily_cap_micros: 5_000_000,
    money: { per_transaction_minor: null, per_day_minor: null },
  },
  nightly_review: {
    window_start: "02:00",
    window_end: "04:00",
    concurrency: 5,
    cost_cap_micros: 250_000,
    max_proposals: 3,
    model: "deepseek-flash",
  },
  failure_stop: { enabled: true, same_refusal_limit: 2, streak_limit: 3, run_limit: 5 },
  recall_tenant: { enabled: false, tenant_name: null },
  skillsmith: {
    time_zone: "America/Chicago",
    daily_at: "07:00",
    weekly_at: "07:00",
    monthly_at: "07:00",
    // SKILLSMITH-CONTROL: the pending decisions at their conservative defaults (settings.ts).
    mail: { daily: false, weekly: false, monthly: false },
    model: "deepseek-flash",
    think_harder: false,
    standing_orders_per_company: 10,
    questions_per_day: 50,
    max_tool_calls: 8,
    cache_minutes: 5,
    allowed_actions: [
      "stop_run",
      "pause_schedule",
      "resume_schedule",
      "rewake_task",
      "ack_alert",
      "resolve_alert",
      "retry_operator_job",
    ],
  },
  live_canvas: { enabled: false },
  task_budgets: {
    enabled: false,
    de: { steps: 60, cost_micros: 1_000_000, time_ms: 1_800_000 },
    helper: { steps: 30, cost_micros: 500_000, time_ms: 900_000 },
  },
};

const DE_NAMES: Record<string, string> = {
  [DE_IDS.riley]: "Riley",
  [DE_IDS.sam]: "Sam",
  [DE_IDS.bea]: "Bea",
  [DE_IDS.gus]: "Gus",
};

const OPERATOR_APIS: readonly Api[] = [
  "companies.from_pack",
  "baseline.apply",
  "jobs.read",
  "jobs.report",
];

type Version = {
  id: string;
  version: number;
  value: unknown;
  note: string | null;
  created_at: string;
  created_by_user_id: string | null;
};
type Slot = { versions: Version[]; current: number | null };
type Placement = {
  satellites: { id: string; name: string }[];
  endpoints: {
    company_id: string;
    de_membership_id: string | null;
    satellite_id: string;
    container: string;
    profile: string;
  }[];
};

/** A stable 64-hex digest (FNV-1a, eight rounds) of a text: the fake's `version` hash. */
function digest(text: string): string {
  let out = "";
  for (let round = 0; round < 8; round++) {
    let h = 0x811c9dc5 ^ round;
    for (let i = 0; i < text.length; i++) {
      h ^= text.charCodeAt(i);
      h = Math.imul(h, 0x01000193) >>> 0;
    }
    out += h.toString(16).padStart(8, "0");
  }
  return out;
}

/**
 * null: a platform admin; else the refusal (MONITORING-P1's guard rules share the rule): an
 * agency user who is not one is 403, anyone else 404.
 */
export function refuseNonPlatformAdmin(me: MeResponse): Response | null {
  if (!me.agency) return notFound();
  // The platform agency's admins (even with no organisations), or an agency admin holding a
  // live operator grant on the platform company.
  const htva =
    PLATFORM_AGENCY_IDS.includes(me.agency.agency_id) ||
    me.agency.grants.some((g) => g.company_id === COMPANY_IDS.htva && g.level === "operator");
  if (me.agency.role !== "admin" || !htva)
    return problem(403, "forbidden", "Forbidden", "The operator console is for platform admins");
  return null;
}

export function createOperatorConsoleFake(deps: { now: () => Date; placement: () => Placement }) {
  let seq = 0;
  const nextId = () => uid(0x0c0e, ++seq);
  const at = () => deps.now().toISOString();
  const slots = new Map<string, Slot>();
  const slotKey = (key: Key, scope: Scope, id: string | null) => `${key}|${scope}|${id ?? ""}`;
  const slotOf = (key: Key, scope: Scope, id: string | null) => {
    const k = slotKey(key, scope, id);
    let s = slots.get(k);
    if (!s) {
      s = { versions: [], current: null };
      slots.set(k, s);
    }
    return s;
  };
  /** The value stored at a scope now (null: none, or cleared). */
  const storedAt = (key: Key, scope: Scope, id: string | null): unknown => {
    const s = slots.get(slotKey(key, scope, id));
    if (!s || s.current === null) return null;
    return s.versions.find((v) => v.version === s.current)?.value ?? null;
  };

  const refuse = refuseNonPlatformAdmin;

  const body = (b: unknown) => (b && typeof b === "object" ? (b as Record<string, unknown>) : {});
  const uuidOrNull = (v: unknown) =>
    typeof v === "string" && /^[0-9a-f-]{36}$/i.test(v) ? v : null;

  function resolve(key: Key, companyId: string | null, satelliteId: string | null) {
    // The server's order: company override > satellite override > platform > default.
    const fromCo = companyId ? storedAt(key, "company", companyId) : null;
    if (fromCo !== null) return { value: fromCo, source: "company" as const };
    const fromSat = satelliteId ? storedAt(key, "satellite", satelliteId) : null;
    if (fromSat !== null) return { value: fromSat, source: "satellite" as const };
    const fromPlatform = storedAt(key, "platform", null);
    if (fromPlatform !== null) return { value: fromPlatform, source: "platform" as const };
    return { value: isProv(key) ? FAKE_SETTING_DEFAULTS[key] : null, source: "default" as const };
  }

  function detail(key: Key, companyId: string | null, satelliteId: string | null): Detail {
    const scope: Scope = satelliteId ? "satellite" : companyId ? "company" : "platform";
    const scopeId = satelliteId ?? companyId ?? null;
    const slot = slots.get(slotKey(key, scope, scopeId));
    const own = storedAt(key, scope, scopeId);
    const r = resolve(key, companyId, satelliteId);
    return {
      key,
      scopes: [...scopesOf(key)],
      editable: !OWN_STORE.includes(key),
      scope,
      scope_id: scopeId,
      default: isProv(key) ? FAKE_SETTING_DEFAULTS[key] : null,
      platform: storedAt(key, "platform", null),
      override: scope === "platform" ? null : own,
      effective: r.value,
      source: r.source,
      version: own !== null ? (slot?.current ?? null) : null,
      history: [...(slot?.versions ?? [])]
        .reverse()
        .slice(0, 50)
        .map((v) => ({
          id: v.id,
          version: v.version,
          value: v.value,
          note: v.note,
          created_at: v.created_at,
          created_by_user_id: v.created_by_user_id,
          created_by_agent_id: null,
          current: v.version === slot?.current,
        })),
    };
  }

  /** The key and scope of a request, or the refusal. */
  function target(
    rawKey: string | undefined,
    companyId: string | null,
    satelliteId: string | null,
  ): { key: Key } | Response {
    const key = ALL_KEYS.find((k) => k === rawKey);
    if (!key) return notFound();
    if (companyId && satelliteId) return validation("company_id or satellite_id, not both");
    const scope: Scope = satelliteId ? "satellite" : companyId ? "company" : "platform";
    if (!scopesOf(key).includes(scope)) return validation(`${key}: no ${scope} scope`);
    if (satelliteId && !deps.placement().satellites.some((s) => s.id === satelliteId))
      return notFound();
    return { key };
  }

  // The rendered baseline ------------------------------------------------------------------------
  type Profile = "light" | "standard" | "heavy";
  type Runtime = Record<string, number>;
  function renderBaseline(companyId: string | null, satelliteId: string | null) {
    const base = resolve("baseline", companyId, satelliteId).value as {
      version: string | null;
      profile: Profile;
      containers: { container: string; version: string | null; profile: Profile | null }[];
    };
    const profiles = resolve("runtime_profiles", null, satelliteId).value as Record<
      Profile,
      Runtime
    >;
    const sources: Record<string, number> = {};
    for (const key of [
      "baseline",
      "runtime_profiles",
      "models",
      "base_prompt",
      "skills_defaults",
      "bridge_runtime",
    ] as const)
      for (const [scope, id] of [
        ["platform", null],
        ["company", companyId],
        ["satellite", satelliteId],
      ] as const) {
        if (scope !== "platform" && !id) continue;
        const s = slots.get(slotKey(key, scope, id));
        if (s?.current && storedAt(key, scope, id) !== null) sources[`${key}@${scope}`] = s.current;
      }
    const baseline = {
      baseline_version: base.version,
      profile: base.profile,
      runtime: profiles[base.profile],
      containers: base.containers.map((c) => ({
        container: c.container,
        baseline_version: c.version ?? base.version,
        profile: c.profile ?? base.profile,
        runtime: profiles[c.profile ?? base.profile],
      })),
      models: resolve("models", companyId, null).value,
      base_prompt_version: (
        resolve("base_prompt", companyId, null).value as { version: string | null }
      ).version,
      skills: (resolve("skills_defaults", null, null).value as { packs: unknown[] }).packs,
      wake: resolve("bridge_runtime", companyId, satelliteId).value,
      sources,
    };
    return { version: digest(JSON.stringify(baseline)), ...baseline };
  }

  // Operator agents --------------------------------------------------------------------------------
  type AgentRow = Omit<AgentView, "has_key" | "key_hint" | "model_kind" | "tokens"> & {
    key: string | null;
    tokens: (AgentView["tokens"][number] & { secret: string })[];
  };
  const seededAt = deps.now().toISOString();
  const agents: AgentRow[] = [
    {
      id: nextId(),
      slug: "deployer",
      name: "Deployer",
      purpose:
        "Sets up new companies from a pack and applies the baseline to satellites, step by step, until the health check is green.",
      label: "system",
      model_setting: "gateway:deepseek-flash",
      allowed_apis: ["companies.from_pack", "baseline.apply", "jobs.read", "jobs.report"],
      daily_cap_micros: 5_000_000,
      spent_today_micros: 0,
      status: "active",
      key: null,
      tokens: [],
      updated_at: seededAt,
    },
    {
      id: nextId(),
      slug: "fixer",
      name: "Fixer",
      purpose:
        "Reads the fleet's issue reports and feedback and proposes setting changes for review.",
      label: "system",
      model_setting: "gateway:deepseek-flash",
      allowed_apis: ["jobs.read"],
      daily_cap_micros: 5_000_000,
      spent_today_micros: 0,
      status: "paused",
      key: null,
      tokens: [],
      updated_at: seededAt,
    },
    {
      id: nextId(),
      slug: "builder",
      name: "Builder",
      purpose: "Builds platform changes from approved proposals, as operator work only.",
      label: "system",
      model_setting: "gateway:deepseek-flash",
      allowed_apis: ["jobs.read"],
      daily_cap_micros: 5_000_000,
      spent_today_micros: 0,
      status: "paused",
      key: null,
      tokens: [],
      updated_at: seededAt,
    },
  ];
  const agentView = (a: AgentRow): AgentView => ({
    id: a.id,
    slug: a.slug,
    name: a.name,
    purpose: a.purpose,
    label: "system",
    model_setting: a.model_setting,
    model_kind: a.model_setting.split(":")[0] as AgentView["model_kind"],
    has_key: a.key !== null,
    key_hint: a.key ? a.key.slice(-4) : null,
    allowed_apis: [...a.allowed_apis],
    daily_cap_micros: a.daily_cap_micros,
    spent_today_micros: a.spent_today_micros,
    status: a.status,
    tokens: a.tokens.map(({ secret: _s, ...t }) => ({ ...t, apis: [...t.apis] })),
    updated_at: a.updated_at,
  });
  const agentOf = (slug: string | undefined) => agents.find((a) => a.slug === slug) ?? null;
  /** The Deployer must be active and allowed the api; else the 409. */
  const deployerRefusal = (api: Api): Response | null => {
    const d = agentOf("deployer");
    if (d?.status !== "active")
      return problem(409, "conflict", "Conflict", "The Deployer is paused");
    if (!d.allowed_apis.includes(api))
      return problem(409, "conflict", "Conflict", `The Deployer is not allowed ${api}`);
    return null;
  };

  // Jobs -------------------------------------------------------------------------------------------
  const jobs: Job[] = [];
  const step = (name: string, state: Step["state"], detail: string): Step => ({
    step: name,
    state,
    detail,
    at: state === "pending" ? null : at(),
  });
  const newJob = (
    kind: Job["kind"],
    me: MeResponse,
    payload: Record<string, unknown>,
    companyId: string | null,
    steps: Step[],
    result: Record<string, unknown> | null,
  ): Job => {
    const failed = steps.find((s) => s.state === "failed");
    const now = at();
    const job: Job = {
      id: nextId(),
      kind,
      state: failed ? "failed" : "done",
      agent: "deployer",
      company_id: companyId,
      payload,
      steps,
      result: failed ? null : result,
      last_error: failed ? failed.detail : null,
      attempts: 1,
      requested_by_user_id: me.user.id,
      created_at: now,
      updated_at: now,
      finished_at: now,
    };
    jobs.unshift(job);
    return job;
  };

  // Margin by model ---------------------------------------------------------------------------------
  // Acme, the demo company the other twins seed runs for, has this month's runs on two models.
  function margin(month: string, companyId: string | null, me: MeResponse) {
    const current = at().slice(0, 7);
    const name =
      me.agency?.grants.find((g) => g.company_id === COMPANY_IDS.acme)?.company_name ??
      "Acme Recruiting";
    const rows =
      month === current && (!companyId || companyId === COMPANY_IDS.acme)
        ? [
            { model: "deepseek-flash", runs: 42, cost_micros: 1_260_000, price_micros: 2_100_000 },
            { model: "claude-sonnet", runs: 6, cost_micros: 2_340_000, price_micros: 3_510_000 },
          ].map((r) => ({
            company_id: COMPANY_IDS.acme,
            company_name: name,
            ...r,
            margin_micros: r.price_micros - r.cost_micros,
          }))
        : [];
    const sum = (list: typeof rows) =>
      list.reduce(
        (t, r) => ({
          runs: t.runs + r.runs,
          cost_micros: t.cost_micros + r.cost_micros,
          price_micros: t.price_micros + r.price_micros,
          margin_micros: t.margin_micros + r.margin_micros,
        }),
        { runs: 0, cost_micros: 0, price_micros: 0, margin_micros: 0 },
      );
    const models = [...new Set(rows.map((r) => r.model))];
    return {
      month,
      rows,
      by_model: models.map((m) => ({ model: m, ...sum(rows.filter((r) => r.model === m)) })),
      totals: sum(rows),
    };
  }

  const handlers: Partial<Record<RouteId, Handler>> = {
    "operator.settings.list": ({ me }) => {
      const no = refuse(me);
      if (no) return no;
      return json({
        settings: ALL_KEYS.map((key) => {
          const platform = slots.get(slotKey(key, "platform", null));
          let overrides = 0;
          let updated: string | null = null;
          for (const [k, s] of slots) {
            if (!k.startsWith(`${key}|`)) continue;
            const last = s.versions.at(-1)?.created_at ?? null;
            if (last && (!updated || last > updated)) updated = last;
            if (!k.startsWith(`${key}|platform|`) && s.current !== null) {
              const [, scope, id] = k.split("|");
              if (storedAt(key, scope as Scope, id || null) !== null) overrides += 1;
            }
          }
          return {
            key,
            scopes: [...scopesOf(key)],
            editable: !OWN_STORE.includes(key),
            version: storedAt(key, "platform", null) !== null ? (platform?.current ?? null) : null,
            overrides,
            updated_at: updated,
          };
        }),
      });
    },
    "operator.settings.get": ({ me, params, url }) => {
      const no = refuse(me);
      if (no) return no;
      const companyId = uuidOrNull(url.searchParams.get("company_id"));
      const satelliteId = uuidOrNull(url.searchParams.get("satellite_id"));
      const t = target(params.key, companyId, satelliteId);
      if (t instanceof Response) return t;
      return json(detail(t.key, companyId, satelliteId));
    },
    "operator.settings.put": ({ me, params, body: raw }) => {
      const no = refuse(me);
      if (no) return no;
      const b = body(raw);
      const companyId = uuidOrNull(b.company_id);
      const satelliteId = uuidOrNull(b.satellite_id);
      const t = target(params.key, companyId, satelliteId);
      if (t instanceof Response) return t;
      const key = t.key;
      if (!isProv(key))
        return problem(409, "conflict", "Conflict", `${key} is edited on its own page`);
      if (!("value" in b)) return validation("value: required (null clears)");
      let value: unknown = b.value;
      if (value !== null) {
        const parsed = ProvisioningSettingValues[key].safeParse(value);
        if (!parsed.success)
          return validation(
            `value: ${parsed.error.issues
              .map((i) => `${i.path.join(".") || key} ${i.message}`)
              .join("; ")}`,
          );
        value = parsed.data;
        const containers = (value as { containers?: unknown }).containers;
        if (
          key === "baseline" &&
          !satelliteId &&
          Array.isArray(containers) &&
          containers.length > 0
        )
          return validation("value.containers: only a satellite baseline lists containers");
      }
      const note = typeof b.note === "string" && b.note.trim() ? b.note.trim().slice(0, 500) : null;
      const scope: Scope = satelliteId ? "satellite" : companyId ? "company" : "platform";
      const scopeId = satelliteId ?? companyId ?? null;
      const before = storedAt(key, scope, scopeId);
      if (JSON.stringify(before) !== JSON.stringify(value)) {
        const slot = slotOf(key, scope, scopeId);
        const version = (slot.versions.at(-1)?.version ?? 0) + 1;
        slot.versions.push({
          id: nextId(),
          version,
          value,
          note,
          created_at: at(),
          created_by_user_id: me.user.id,
        });
        slot.current = version;
      }
      return json(detail(key, companyId, satelliteId));
    },
    "operator.settings.rollback": ({ me, params, body: raw }) => {
      const no = refuse(me);
      if (no) return no;
      const b = body(raw);
      const companyId = uuidOrNull(b.company_id);
      const satelliteId = uuidOrNull(b.satellite_id);
      const t = target(params.key, companyId, satelliteId);
      if (t instanceof Response) return t;
      if (!isProv(t.key))
        return problem(409, "conflict", "Conflict", `${t.key} is edited on its own page`);
      const version = b.version;
      if (typeof version !== "number" || !Number.isInteger(version) || version < 1)
        return validation("version: a version number");
      const scope: Scope = satelliteId ? "satellite" : companyId ? "company" : "platform";
      const slot = slots.get(slotKey(t.key, scope, satelliteId ?? companyId ?? null));
      if (!slot?.versions.some((v) => v.version === version)) return notFound();
      slot.current = version;
      return json(detail(t.key, companyId, satelliteId));
    },
    "operator.baseline.get": ({ me, url }) => {
      const no = refuse(me);
      if (no) return no;
      const companyId = uuidOrNull(url.searchParams.get("company_id"));
      const satelliteId = uuidOrNull(url.searchParams.get("satellite_id"));
      if (satelliteId && !deps.placement().satellites.some((s) => s.id === satelliteId))
        return notFound();
      return json({
        scope: satelliteId ? "satellite" : companyId ? "company" : "platform",
        scope_id: satelliteId ?? companyId ?? null,
        generated_at: at(),
        baseline: renderBaseline(companyId, satelliteId),
      });
    },
    "operator.roster.get": ({ me, url }) => {
      const no = refuse(me);
      if (no) return no;
      const companyId = uuidOrNull(url.searchParams.get("company_id"));
      if (!companyId) return validation("company_id: required");
      if (!me.agency?.grants.some((g) => g.company_id === companyId)) return notFound();
      const place = deps.placement();
      const overrides =
        (
          storedAt("roster_overrides", "company", companyId) as {
            des: {
              membership_id: string;
              satellite_id: string | null;
              container: string | null;
              profile: string | null;
              model: string | null;
              runtime_profile: Profile | null;
            }[];
          } | null
        )?.des ?? [];
      const slot = slots.get(slotKey("roster_overrides", "company", companyId));
      const des = place.endpoints
        .filter((e) => e.company_id === companyId && e.de_membership_id)
        .map((e) => {
          const id = e.de_membership_id as string;
          const o = overrides.find((x) => x.membership_id === id);
          const satelliteId = o?.satellite_id ?? e.satellite_id;
          const base = resolve("baseline", companyId, satelliteId).value as { profile: Profile };
          const overridden: ("placement" | "model" | "runtime_profile")[] = [];
          if (o && (o.satellite_id || o.container || o.profile)) overridden.push("placement");
          if (o?.model) overridden.push("model");
          if (o?.runtime_profile) overridden.push("runtime_profile");
          return {
            membership_id: id,
            name: DE_NAMES[id] ?? "DE",
            satellite_id: satelliteId,
            satellite_name: place.satellites.find((s) => s.id === satelliteId)?.name ?? null,
            container: o?.container ?? e.container,
            profile: o?.profile ?? e.profile,
            model: o?.model ?? null,
            runtime_profile: o?.runtime_profile ?? base.profile,
            has_key: true,
            overridden,
          };
        });
      return json({
        company_id: companyId,
        generated_at: at(),
        des,
        overrides_version:
          storedAt("roster_overrides", "company", companyId) !== null
            ? (slot?.current ?? null)
            : null,
      });
    },
    "operator.margin.models": ({ me, url }) => {
      const no = refuse(me);
      if (no) return no;
      const month = url.searchParams.get("month") ?? "";
      if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(month)) return validation("month: YYYY-MM");
      return json(margin(month, uuidOrNull(url.searchParams.get("company_id")), me));
    },
    "operator.agents.list": ({ me }) =>
      refuse(me) ?? json({ agents: agents.map(agentView), apis: [...OPERATOR_APIS] }),
    "operator.agents.put": ({ me, params, body: raw }) => {
      const no = refuse(me);
      if (no) return no;
      const a = agentOf(params.slug);
      if (!a) return notFound();
      const parsed = OperatorAgentPut.safeParse(raw);
      if (!parsed.success) return validation(parsed.error.issues.map((i) => i.message).join("; "));
      const p = parsed.data;
      // A subscription model is the Builder's alone (and never an agent tied to a company).
      if (p.model_setting?.startsWith("subscription:") && a.slug !== "builder")
        return problem(409, "conflict", "Conflict", "A subscription model is for the Builder only");
      if (p.model_setting !== undefined) a.model_setting = p.model_setting;
      if (p.allowed_apis !== undefined) a.allowed_apis = [...new Set(p.allowed_apis)];
      if (p.daily_cap_micros !== undefined) a.daily_cap_micros = p.daily_cap_micros;
      if (p.status !== undefined) a.status = p.status;
      a.updated_at = at();
      return json(agentView(a));
    },
    "operator.agents.key.put": ({ me, params, body: raw }) => {
      const no = refuse(me);
      if (no) return no;
      const a = agentOf(params.slug);
      if (!a) return notFound();
      const parsed = OperatorAgentKeyPut.safeParse(raw);
      if (!parsed.success) return validation("key: 20 to 400 characters, or null to remove");
      a.key = parsed.data.key;
      a.updated_at = at();
      return json(agentView(a));
    },
    "operator.agents.tokens.create": ({ me, params, body: raw }) => {
      const no = refuse(me);
      if (no) return no;
      const a = agentOf(params.slug);
      if (!a) return notFound();
      const parsed = OperatorJobTokenCreate.safeParse(raw);
      if (!parsed.success)
        return validation("label (1–60), apis (at least one) and days (1–90) are required");
      const p = parsed.data;
      const notAllowed = p.apis.filter((x) => !a.allowed_apis.includes(x));
      if (notAllowed.length > 0)
        return problem(
          409,
          "conflict",
          "Conflict",
          `The agent is not allowed: ${notAllowed.join(", ")}`,
        );
      const secret = `cosop_${digest(`${a.slug}:${++seq}:${at()}`).slice(0, 40)}`;
      const now = deps.now();
      a.tokens.unshift({
        id: nextId(),
        prefix: secret.slice(0, 12),
        label: p.label,
        apis: [...new Set(p.apis)],
        expires_at: new Date(now.getTime() + p.days * 86_400_000).toISOString(),
        revoked_at: null,
        last_used_at: null,
        created_at: now.toISOString(),
        secret,
      });
      return json({ token: secret, agent: agentView(a) }, 201);
    },
    "operator.agents.tokens.revoke": ({ me, params }) => {
      const no = refuse(me);
      if (no) return no;
      const a = agentOf(params.slug);
      const t = a?.tokens.find((x) => x.id === params.id);
      if (!a || !t) return notFound();
      t.revoked_at ??= at();
      return json(agentView(a));
    },
    "operator.jobs.list": ({ me, url }) => {
      const no = refuse(me);
      if (no) return no;
      const state = url.searchParams.get("state");
      return json({ jobs: jobs.filter((j) => !state || j.state === state).slice(0, 100) });
    },
    "operator.jobs.get": ({ me, params }) => {
      const no = refuse(me);
      if (no) return no;
      const j = jobs.find((x) => x.id === params.id);
      return j ? json(j) : notFound();
    },
    "operator.jobs.retry": ({ me, params }) => {
      const no = refuse(me);
      if (no) return no;
      const j = jobs.find((x) => x.id === params.id);
      if (!j) return notFound();
      if (j.state !== "failed")
        return problem(409, "conflict", "Conflict", "Only a failed job can be retried");
      j.state = "queued";
      j.last_error = null;
      // as the API: a retry starts the attempt count over
      j.attempts = 0;
      j.finished_at = null;
      j.updated_at = at();
      j.steps = j.steps.map((s) =>
        s.state === "failed" ? { ...s, state: "pending", at: null } : s,
      );
      return json(j);
    },
    "operator.deployer.companyFromPack": ({ me, body: raw }) => {
      const no = refuse(me);
      if (no) return no;
      const parsed = CompanyFromPackRequest.safeParse(raw);
      if (!parsed.success)
        return validation(
          parsed.error.issues.map((i) => `${i.path.join(".")} ${i.message}`).join("; "),
        );
      const idle = deployerRefusal("companies.from_pack");
      if (idle) return idle;
      const pack = parsed.data.pack;
      if (me.agency?.grants.some((g) => g.company_slug === pack.slug))
        return problem(409, "conflict", "Conflict", `The slug ${pack.slug} is taken`);
      const companyId = nextId();
      const sat = pack.satellite_id
        ? deps.placement().satellites.find((s) => s.id === pack.satellite_id)
        : null;
      if (pack.satellite_id && !sat) return validation("pack.satellite_id: not a satellite");
      const job = newJob(
        "company_from_pack",
        me,
        { pack },
        companyId,
        [
          step(
            "company",
            "done",
            `${pack.name} created (${pack.slug})${pack.owner ? `, ${pack.owner.email} invited` : ""}`,
          ),
          step("settings", "done", "Company settings applied"),
          step("provision", "done", "System agent and feature rules set up"),
          sat
            ? step("placement", "done", `Placed on ${sat.name}`)
            : step("placement", "skipped", "Placement decided later"),
          step("clawpanel", "skipped", "ClawPanel does not offer company set-up yet"),
          step("baseline", "skipped", "ClawPanel does not offer apply-baseline yet"),
          step("health", "done", "Health check green"),
        ],
        { company_id: companyId, slug: pack.slug },
      );
      return json(job, 201);
    },
    "operator.deployer.applyBaseline": ({ me, body: raw }) => {
      const no = refuse(me);
      if (no) return no;
      const parsed = ApplyBaselineRequest.safeParse(raw);
      if (!parsed.success) return validation("satellite_id and dry_run are required");
      const idle = deployerRefusal("baseline.apply");
      if (idle) return idle;
      const r = parsed.data;
      const place = deps.placement();
      const sat = place.satellites.find((s) => s.id === r.satellite_id);
      if (!sat) return notFound();
      const containers = [
        ...new Set(
          place.endpoints.filter((e) => e.satellite_id === sat.id).map((e) => e.container),
        ),
      ];
      const rendered = renderBaseline(null, sat.id);
      const known = !r.container || containers.includes(r.container);
      const scope =
        r.container ?? `${containers.length} container${containers.length === 1 ? "" : "s"}`;
      // The server's steps: render, idle (skipped on a dry run), apply.
      const steps: Step[] = [
        known
          ? step(
              "render",
              "done",
              `Baseline ${rendered.version.slice(0, 12)} rendered for ${scope}`,
            )
          : step("render", "failed", `No container ${r.container} on ${sat.name}`),
        !known
          ? step("idle", "pending", "")
          : r.dry_run
            ? step("idle", "skipped", "Dry run")
            : step("idle", "done", `${scope} idle`),
        !known
          ? step("apply", "pending", "")
          : step("apply", "skipped", "ClawPanel does not offer apply-baseline yet"),
      ];
      const job = newJob("apply_baseline", me, { ...r }, null, steps, {
        baseline_version: rendered.version,
        dry_run: r.dry_run,
        drift: [],
      });
      return json(job, 201);
    },
    "operator.baseRules.get": ({ me }) =>
      me.agency
        ? json({
            version: DE_BASE_RULES_VERSION,
            rules: baseRuleRows(),
            de_text: DE_BASE_PROMPT,
            ag_text: AG_BASE_PROMPT,
            markdown: baseRulesMarkdown(),
          })
        : notFound(),
  };
  return { handlers };
}
